/**
 * Helpers de formatação do módulo billing: moeda BRL (Money = string decimal
 * "1234.56" no wire), horas HH:MM e competência YYYY-MM.
 */

// ── Moeda ───────────────────────────────────────────────────────────────────

/** '1234.56' → 'R$ 1.234,56'. */
export function formatBRL(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Aceita '1.234,56', '1234,56', '1234.56', '1234' e 'R$ 1.234,56' → string
 * decimal '1234.56' (formato Money do wire). Retorna null se inválido.
 */
export function parseBRLInput(raw: string): string | null {
  const s = raw.trim().replace(/^R\$\s*/i, '');
  if (s === '') return null;
  let normalized: string;
  if (s.includes(',')) {
    // pt-BR: '.' é milhar, ',' é decimal.
    normalized = s.replace(/\./g, '').replace(',', '.');
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) {
    // Só pontos em grupos de 3 → milhares pt-BR ('1.234' = 1234).
    normalized = s.replace(/\./g, '');
  } else {
    normalized = s;
  }
  if (!/^-?\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return normalized;
}

/** Valor decimal p/ exibição em input de moeda: '1234.5' → '1.234,50'. */
export function formatBRLInput(decimal: string): string {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function moneyToCents(value: string): number {
  return Math.round(Number(value) * 100);
}

export function centsToMoney(cents: number): string {
  return (cents / 100).toFixed(2);
}

// ── Horas (HH:MM ↔ minutos) ─────────────────────────────────────────────────

export function minutesToHHMM(min: number): string {
  const sign = min < 0 ? '-' : '';
  const abs = Math.abs(min);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** '40:30' → 2430; aceita também só horas ('40' → 2400). Null se inválido. */
export function hhmmToMinutes(value: string): number | null {
  const m = /^(\d{1,4})(?::([0-5]?\d))?$/.exec(value.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + (m[2] !== undefined ? Number(m[2]) : 0);
}

// ── Datas / competência ─────────────────────────────────────────────────────

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

export function todayISO(): string {
  return toISO(new Date());
}

export function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toISO(d);
}

/** Competência do mês atual no formato YYYY-MM. */
export function currentCompetence(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** '2026-08' → '08/2026'. */
export function formatCompetence(competence: string): string {
  const [year, month] = competence.split('-');
  return `${month}/${year}`;
}

/** '2026-08-14' → '14/08/2026' (split puro, sem timezone). */
export function formatDateBR(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/** 'dd/mm/aaaa — dd/mm/aaaa' ou 'desde dd/mm/aaaa' quando sem expiração. */
export function formatVigencia(startDate: string, expirationDate: string | null): string {
  return expirationDate
    ? `${formatDateBR(startDate)} — ${formatDateBR(expirationDate)}`
    : `desde ${formatDateBR(startDate)}`;
}
